/**
 * What the page says about a repository, worked out before anything renders:
 * the one word in the pill, the sentence under it, and where the fix button on
 * a failed row sends a person.
 *
 * Reading the record and wording it are different jobs. Only the first is
 * worth testing, and it reads as a table when it is kept out of the markup.
 */
import type {
  QaAsk,
  QaChecklistRow,
  QaEnvironmentConfig,
  QaEnvironmentLine,
  QaEnvironmentState,
  QaEnvironmentView,
  QaSetupNeed,
  QaRunStep,
  QaValidation,
  RepoIndex,
  RepoIndexCheckRequirement,
  CapabilityCheckStatus,
  CapabilityDependencyStatus,
  CapabilityEvidence,
  CapabilityMissing,
  CapabilitySource,
  RepoCapabilityRecord,
  WorkspaceVariable,
} from "@yagni/shared";
import {
  capabilityRecordStale,
  qaEndingFailedStep,
  qaEnvironmentSchema,
  qaStepDuration,
} from "@yagni/shared";

/** A name a shell would take as a variable: what every variable form accepts. */
export function isVariableName(name: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name);
}

/** The `StatusPill` tones this page uses, named as the pill names them. */
export type EnvironmentPillTone = "neutral" | "executing" | "needsYou" | "done";

export interface EnvironmentPill {
  label: string;
  tone: EnvironmentPillTone;
  /** The breathing dot: nothing moves until a person does something. */
  pulse?: boolean;
}

/**
 * Blue is human attention, so only `needs_you` gets it. A run in flight is the
 * sandbox working, and a working environment is settled: neither competes for
 * the eye, and amber never reaches the pill because amber lives on the failed
 * row of the list, where the product is telling on itself.
 */
export const ENVIRONMENT_PILLS: Record<QaEnvironmentState, EnvironmentPill> = {
  not_set_up: { label: "Not set up", tone: "neutral" },
  setting_up: { label: "Setting up", tone: "executing" },
  needs_you: { label: "Needs you", tone: "needsYou", pulse: true },
  working: { label: "Working", tone: "done" },
};

/** A Working environment that runs no browser is ready for builds, and its pill says so. */
export const BUILD_ONLY_PILL: EnvironmentPill = {
  label: "Builds ready",
  tone: "done",
};

export function environmentPill(
  state: QaEnvironmentState | undefined,
  buildOnly = false,
): EnvironmentPill {
  if (state === "working" && buildOnly) return BUILD_ONLY_PILL;
  return (
    (state === undefined ? undefined : ENVIRONMENT_PILLS[state]) ?? ENVIRONMENT_PILLS.not_set_up
  );
}

/**
 * A run is in flight: setting up, or waiting on a private sign-in. The pill
 * reads the second as Needs you, but it is the same run: still stoppable,
 * still polled, and still the reason the button says Stop.
 */
export function isRunInFlight(view: QaEnvironmentView | null | undefined): boolean {
  return view?.state === "setting_up" || view?.status === "validating";
}

/** The word a run wears in the list of previous runs. */
export const RUN_STATUS_WORDS: Record<QaValidation["status"], string> = {
  validating: "Running",
  ready: "Worked",
  needs_attention: "Needs you",
};

export function formatEnvironmentDate(iso: string | undefined): string {
  const at = iso ? new Date(iso) : null;
  return at && Number.isFinite(at.getTime())
    ? at.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "an earlier date";
}

/** "a, b and c", because a list of Teams is read aloud, not tabulated. */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export interface EnvironmentHeadline {
  /** True whatever state the repository is in: what this environment is for. */
  lead: string;
  /** What that means today: who is waiting, or what it already does. */
  detail?: string;
  /** No Team attaches this repository, so the sentence offers one instead. */
  attach?: { text: string; href: string };
}

export const ENVIRONMENT_LEAD =
  "Fletcher checks pull requests and Wright builds in this environment.";

/** "14:05" in the reader's own clock, or null when the time is unreadable. */
export function formatEnvironmentTime(iso: string | undefined): string | null {
  const at = iso ? new Date(iso) : null;
  return at && Number.isFinite(at.getTime())
    ? at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : null;
}

/**
 * The run is waiting on a person to sign in privately: the one thing the
 * page asks for, with the time the sign-in stops being accepted.
 */
function signInDetail(view: QaEnvironmentView): string | null {
  const validation = view.draft?.validation ?? view.latestRun?.validation;
  if (
    view.state !== "needs_you" ||
    validation?.status !== "validating" ||
    !validation.interactiveSessionId
  )
    return null;
  const before = formatEnvironmentTime(validation.signInExpiresAt ?? validation.deadlineAt);
  return before ? `Sign in privately before ${before}.` : "Sign in privately to continue.";
}

/**
 * The sentence under the pill. The Teams named are the ones that use this
 * environment, per line: Fletcher's PR checks and Wright's builds (the
 * repository attached and the line on), not every Team that attaches the
 * repository: Proctor reviews the diff in its own checkout and never touches
 * this environment. A repository nobody depends on says so and points at the
 * Teams page; a run waiting on a private sign-in says by when; everything
 * else names who is waiting, or what the environment has been doing since it
 * started working.
 */
export function environmentHeadline(view: QaEnvironmentView): EnvironmentHeadline {
  const signIn = signInDetail(view);
  if (signIn) return { lead: ENVIRONMENT_LEAD, detail: signIn };
  const teams = view.uses?.teams ?? [];
  if (!teams.length)
    return {
      lead: ENVIRONMENT_LEAD,
      attach: {
        text: "Turn on Fletcher's PR checks or Wright's builds on a Team that attaches this repository to put it to work.",
        href: "/teams",
      },
    };
  const named = (line: QaEnvironmentLine) =>
    joinNames(
      teams.filter((team) => (team.lines ?? ["pr_checks"]).includes(line)).map((team) => team.name),
    );
  const checks = named("pr_checks"),
    builds = named("builds");
  if (view.state === "working") {
    const uses = [
      checks ? `Fletcher checks PRs on ${checks}` : null,
      builds ? `Wright builds on ${builds}` : null,
    ].filter(Boolean);
    if (!uses.length) return { lead: ENVIRONMENT_LEAD };
    return {
      lead: ENVIRONMENT_LEAD,
      detail: `${uses.join("; ")} since ${formatEnvironmentDate(view.working?.since)}.`,
    };
  }
  const waiting = view.uses?.waitingPrs ?? 0;
  if (waiting > 0)
    return {
      lead: ENVIRONMENT_LEAD,
      detail: `${waiting} open ${waiting === 1 ? "PR" : "PRs"} on ${
        checks || joinNames(teams.map((team) => team.name))
      } ${waiting === 1 ? "is" : "are"} waiting for it.`,
    };
  return { lead: ENVIRONMENT_LEAD };
}

export interface EnvironmentFix {
  /** The settings row the button opens, by its `section` key. */
  section: "application" | "sign-in" | "credentials" | "network" | "advanced";
  label: string;
}

/**
 * What a person supplies, turned into the one button that takes them to it.
 * `person` has no row to open: the advice is the whole of it.
 */
export const ENVIRONMENT_FIXES: Record<QaSetupNeed, EnvironmentFix | null> = {
  credential: { section: "credentials", label: "Add the credential" },
  origin: { section: "network", label: "Approve the destination" },
  sign_in: { section: "sign-in", label: "Change sign-in" },
  configuration: { section: "application", label: "Change the configuration" },
  person: null,
};

export function environmentFix(needs: QaSetupNeed | undefined): EnvironmentFix | null {
  return (needs === undefined ? null : ENVIRONMENT_FIXES[needs]) ?? null;
}

export const REUSED_SANDBOX = "reused the running environment";

/**
 * "Tried three fixes: …", the run's own repair phase said out loud. Fix 0 is
 * the run reading the repository for how the app starts; it is not one of
 * the three, so it is named rather than counted.
 */
export function fixesTriedLine(fixes: QaValidation["fixes"]): string | undefined {
  if (!fixes?.length) return undefined;
  const read = fixes.some((fix) => fix.number === 0);
  const repairs = fixes.filter((fix) => fix.number !== 0).length;
  const counted = ["one", "two", "three"][repairs - 1] ?? `${repairs}`;
  // A repair tried in the sandbox the run already had, rather than a fresh
  // boot, says so after what it changed.
  const changes = fixes.flatMap((fix) => {
    const own = fix.changes.filter(Boolean);
    if (!fix.reusedSandbox) return own;
    const last = own.at(-1);
    return last ? [...own.slice(0, -1), `${last} (${REUSED_SANDBOX})`] : [REUSED_SANDBOX];
  });
  const noun = repairs === 1 ? "fix" : "fixes";
  const said = [
    read ? "Read the repository" : null,
    repairs ? `${read ? "tried" : "Tried"} ${counted} ${noun}` : null,
  ]
    .filter(Boolean)
    .join(" and ");
  return changes.length ? `${said}: ${changes.join("; ")}.` : `${said}.`;
}

/**
 * Said beside the run, never asked: nothing is ticked before a run starts
 * (YAG-1066). It is what a run does with the app, so it is still said.
 */
export const RECORDING_NOTE =
  "This repository uses dedicated test accounts and isolated data. Runs record video of the app, so anything on screen may be captured; masks are best-effort.";

/** A credential pair is half typed, so the run waits for it. */
export const FINISH_CREDENTIAL = "Finish or clear the credential under Variables to run.";

/** What the one button says, and the line under it. */
export interface EnvironmentRunAction {
  label: string;
  help: string;
}

/**
 * What the page is being asked for, in four booleans.
 *
 * `saved` is "there is a saved configuration to run against", which is not the
 * same as "the form has something in it": until something has been saved or a
 * run has happened, the button is still Set up.
 */
export interface EnvironmentRunState {
  validating: boolean;
  saved: boolean;
  dirty: boolean;
  /** A credential name or value is typed but not yet added to the draft. */
  credentialPending?: boolean;
}

/** The button and its help line, before a half-typed credential has its say. */
function runActionFor(state: EnvironmentRunState): EnvironmentRunAction {
  if (state.validating)
    return {
      label: "Stop",
      help: "Stops this run. The environment in use is unchanged.",
    };
  if (!state.saved)
    return {
      label: "Set up",
      help: "Reads the repository, saves it, and boots it in a disposable sandbox.",
    };
  if (state.dirty)
    return {
      label: "Save and run",
      help: "Saves this configuration and runs it.",
    };
  return {
    label: "Run again",
    help: "Runs the saved configuration again in a disposable sandbox.",
  };
}

/**
 * The button and its help line.
 *
 * Four states read in order, because they are not independent: a run in flight
 * is Stop whatever else is true.
 */
export function runAction(state: EnvironmentRunState): EnvironmentRunAction {
  const action = runActionFor(state);
  // A half-typed credential holds the button back; the line under it says so
  // rather than describing a run that cannot start.
  if (state.credentialPending && !state.validating)
    return { label: action.label, help: FINISH_CREDENTIAL };
  return action;
}

/** What `view.working` carries: the revision PR checks use, and since when. */
export type EnvironmentWorking = QaEnvironmentView["working"];

export interface RowDetailContext {
  /** This row is the one the amber block hangs off. */
  attention: boolean;
  /** The latest run failed, so the amber block carries the cause. */
  failed: boolean;
  working: EnvironmentWorking;
}

/**
 * The second line of a checklist row.
 *
 * Two rows say something other than their own detail: the row the amber block
 * hangs off says its cause there and would otherwise say it twice, and the row
 * that put the environment to work dates it rather than repeating its label.
 */
export function rowDetail(row: QaChecklistRow, context: RowDetailContext): string | undefined {
  if (context.attention && context.failed) return undefined;
  if (row.key === "use" && row.state === "done" && context.working)
    return `Since ${formatEnvironmentDate(context.working.since)}`;
  return row.detail;
}

/**
 * Which checklist row carries the amber block, or null when nothing does.
 *
 * A run waiting on a person to sign in is attention, not a failure, and it
 * always belongs on the sign-in row: that is where the private browser opens,
 * and such a run has no failed row to hang off.
 */
export function attentionRowKey(
  rows: readonly QaChecklistRow[],
  validation: QaValidation | null,
): string | null {
  if (validation?.status === "validating" && validation.interactiveSessionId) return "sign_in";
  return rows.find((row) => row.state === "failed")?.key ?? null;
}

export interface SwitchToContext {
  view: QaEnvironmentView | null;
  latestRun: QaEnvironmentView["latestRun"];
  validation: QaValidation | null;
  dirty: boolean;
  busy: boolean;
  validating: boolean;
}

/**
 * May a person put the newest run to work in place of the one PR checks use?
 *
 * Only when there is a newer one, it worked, and nothing is in flight or
 * unsaved: switching to a revision whose configuration has since been edited
 * would put something to work that nobody has run.
 */
export function canSwitchTo(context: SwitchToContext): boolean {
  const working = context.view?.working;
  if (!working || !context.latestRun) return false;
  if (context.latestRun.revisionId === working.revisionId) return false;
  if (context.validation?.status !== "ready") return false;
  if (recheckedWorking(context.view, context.latestRun)) return false;
  return !context.dirty && !context.busy && !context.validating;
}

/**
 * Whether the newest run is a Validate now that passed on the configuration
 * already in use (YAG-1109). It re-checked the Working revision in a copy of
 * its own, so nothing newer waits to be put to work: the sheet neither says
 * PR checks still use the older run nor offers a switch to the copy.
 */
export function recheckedWorking(
  view: QaEnvironmentView | null,
  latestRun: QaEnvironmentView["latestRun"],
): boolean {
  const draft = view?.draft;
  const active = view?.active;
  if (!draft || !active || !latestRun || draft.id !== latestRun.revisionId) return false;
  if (draft.validation?.startedFor !== "validate" || draft.validation.status !== "ready")
    return false;
  const recheck = comparableConfig(draft.config);
  return recheck !== null && recheck === comparableConfig(active.config);
}

/**
 * A stored configuration as the backend compares two (`sameConfiguration`,
 * `qa/run.ts`): parsed with its defaults filled and the retired consent read
 * as given, keys sorted, so a revision saved before a field had a default
 * still equals its re-checked copy. Null when it does not parse.
 */
function comparableConfig(config: QaEnvironmentConfig): string | null {
  const parsed = qaEnvironmentSchema.safeParse({ ...config, version: 2 });
  if (!parsed.success) return null;
  return JSON.stringify({ ...parsed.data, testDataConfirmed: true }, (_key, value: unknown) =>
    value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(
          Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
        )
      : value,
  );
}

/**
 * The configuration the checklist is read against.
 *
 * The one the latest run actually used, so a failed row names the command that
 * failed rather than whatever is in the form now. A run whose revision is
 * neither the draft nor the active one falls back to the current
 * configuration, which is the only one the page still holds.
 */
export function runConfigFor(
  view: QaEnvironmentView | null,
  latestRun: QaEnvironmentView["latestRun"],
  current: QaEnvironmentConfig,
): QaEnvironmentConfig {
  if (!view || !latestRun) return current;
  if (view.draft?.id === latestRun.revisionId) return view.draft.config;
  if (view.active?.id === latestRun.revisionId) return view.active.config;
  return current;
}

/** The redactor leaves short values alone, so the page says so once. */
export const SHORT_VALUES_NOTE = "Values shorter than 8 characters are not hidden in output.";

export const SEEDED_FROM_LEGACY = "Carried over from the build settings.";
export const BUILDS_USE_LEGACY =
  "Builds use your earlier build settings until this environment is working.";

/**
 * What the page says about a draft carried over from the legacy build
 * settings: where it came from and, until it works, what builds use in the
 * meantime. Nothing for any other draft, or once the environment is working
 * (its first activation retired the legacy settings).
 */
export function seedNotes(view: QaEnvironmentView | null): string[] {
  if (view?.draft?.seededFrom !== "legacy" || view.state === "working") return [];
  return [SEEDED_FROM_LEGACY, BUILDS_USE_LEGACY];
}

/**
 * The second line of the amber block: the run's own words, when they say
 * something the cause does not (a named credential, a host). Nothing when
 * they are the cause again.
 */
export function attentionMessage(
  validation: Pick<QaValidation, "cause" | "message">,
): string | undefined {
  const message = validation.message?.trim();
  if (!message || !validation.cause) return undefined;
  return message === validation.cause.trim() ? undefined : message;
}

/** The host an origin ask names, for the button that approves it. */
export function askHost(origin: string): string {
  try {
    return new URL(origin).host || origin;
  } catch {
    return origin;
  }
}

/**
 * The configuration an origin ask adds its origin to: installs read
 * `installationOrigins`, the app under test `allowedOrigins`. Already listed
 * is a no-op patch, so the run still counts as a person's.
 */
export function originPatch(
  config: QaEnvironmentConfig,
  ask: { origin: string; list: "installation" | "application" },
): Partial<Pick<QaEnvironmentConfig, "installationOrigins" | "allowedOrigins">> {
  if (ask.list === "installation") {
    const current = config.installationOrigins ?? ["https://registry.npmjs.org"];
    return {
      installationOrigins: current.includes(ask.origin) ? current : [...current, ask.origin],
    };
  }
  return {
    allowedOrigins: config.allowedOrigins.includes(ask.origin)
      ? config.allowedOrigins
      : [...config.allowedOrigins, ask.origin],
  };
}

/**
 * What Approve the destination stages: every origin the run asked for, added to
 * the list it names. Only the lists that change are in the patch; null when
 * nothing does (no origin asked, or each one already listed).
 */
export function originFixPatch(
  config: QaEnvironmentConfig,
  asks: QaAsk[],
): Partial<Pick<QaEnvironmentConfig, "installationOrigins" | "allowedOrigins">> | null {
  let next = config;
  for (const ask of asks) if (ask.kind === "origin") next = { ...next, ...originPatch(next, ask) };
  const patch = {
    ...(next.installationOrigins !== config.installationOrigins
      ? { installationOrigins: next.installationOrigins }
      : {}),
    ...(next.allowedOrigins !== config.allowedOrigins
      ? { allowedOrigins: next.allowedOrigins }
      : {}),
  };
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * What support needs to find a failed run without a database query: the
 * revision, the error code, the stage, the reference when there is one, and
 * the output the run kept (YAG-1136). That is the failure's own, else its
 * failed step's, then a red suite's when it is something else.
 */
export function runDetails(
  revisionId: string,
  validation: Pick<
    QaValidation,
    "errorCode" | "stage" | "reference" | "outputTail" | "steps" | "suite"
  >,
): string {
  const output = validation.outputTail ?? qaEndingFailedStep(validation.steps)?.outputTail;
  const suite = validation.suite?.status === "failed" ? validation.suite.outputTail : undefined;
  return [
    `Revision: ${revisionId}`,
    `Error code: ${validation.errorCode ?? "none"}`,
    `Stage: ${validation.stage}`,
    ...(validation.reference ? [`Reference: ${validation.reference}`] : []),
    ...(output ? ["", "Output:", output] : []),
    ...(suite && suite !== output ? ["", "Test suite output:", suite] : []),
  ].join("\n");
}

/**
 * The line a Working environment carries when Wright's last build could not
 * install it. `commandIndex` counts from zero on the record and from one here.
 */
export function lastBuildFailureLine(
  failure: QaEnvironmentView["lastBuildFailure"],
): string | null {
  if (!failure) return null;
  const command =
    typeof failure.commandIndex === "number"
      ? ` (installation command ${failure.commandIndex + 1})`
      : "";
  return `Wright's build on ${formatEnvironmentDate(failure.at)} could not install this environment${command}.`;
}

// ---------------------------------------------------------------------------
// The repository index (YAG-831): the description row, the build warnings,
// the Variables chips and the checks a repository needs.
// ---------------------------------------------------------------------------

/** What the description row shows for an index row, before any markup. */
export type IndexLine =
  | { kind: "indexing" }
  | { kind: "failed"; message: string }
  | { kind: "described"; text: string | null; byPerson: boolean };

export function indexLine(index: RepoIndex | null | undefined): IndexLine {
  if (!index || index.status === "pending") return { kind: "indexing" };
  if (index.status === "failed")
    return {
      kind: "failed",
      message: index.error?.message ?? "This repository could not be read.",
    };
  return {
    kind: "described",
    text: index.description,
    byPerson: index.descriptionSource === "person",
  };
}

/**
 * The runtime a `qa_runtime_unsupported` sentence names ("Cargo.toml needs
 * Rust, which ..." reads as Rust, "needs .NET Framework on Windows" as
 * .NET Framework), or null when the sentence names none the page can pick out.
 */
export function unsupportedRuntime(message: string): string | null {
  const match = /needs (\.NET Framework|[A-Z][\w.+#-]*(?: (?:or|and) [A-Z][\w.+#-]*)?)/.exec(
    message,
  );
  return match?.[1] ?? null;
}

/** One warning line per thing a build cannot do for this repository. */
export interface IndexWarning {
  key: string;
  line: string;
  detail?: string;
}

export function indexWarnings(index: RepoIndex | null | undefined): IndexWarning[] {
  if (!index || index.status !== "indexed") return [];
  const warnings: IndexWarning[] = [];
  index.notes.forEach((note, position) => {
    if (note.code === "qa_runtime_substituted") {
      const runtime = /builds run ([A-Z][\w.+#-]* \d+(?:\.\d+)*)/.exec(note.message)?.[1];
      warnings.push({
        key: `substituted:${position}`,
        line: runtime
          ? `Builds run ${runtime}, not the version this repository requires`
          : "Builds run a different runtime version than this repository requires",
        detail: note.message,
      });
      return;
    }
    if (note.code !== "qa_runtime_unsupported") return;
    const runtime = unsupportedRuntime(note.message);
    warnings.push({
      key: `runtime:${position}`,
      line: runtime
        ? `Builds cannot install ${runtime}`
        : "Builds cannot install a runtime this repository declares",
      detail: note.message,
    });
  });
  for (const origin of index.unreachableOrigins)
    warnings.push({
      key: `origin:${origin}`,
      line: `Builds cannot reach ${askHost(origin)}`,
    });
  return warnings;
}

export type VariableChip = "set_here" | "from_workspace" | "missing";

export const VARIABLE_CHIP_LABELS: Record<VariableChip, string> = {
  set_here: "This repository",
  from_workspace: "Workspace",
  missing: "Missing",
};

/** A repository's own value wins, so this repository is read before the workspace. */
export function variableChip(
  name: string,
  setHere: ReadonlySet<string>,
  workspace: ReadonlySet<string>,
): VariableChip {
  if (setHere.has(name)) return "set_here";
  if (workspace.has(name)) return "from_workspace";
  return "missing";
}

/** One name in the Variables row, read against this repository and the workspace. */
export interface RepositoryVariableRow {
  name: string;
  chip: VariableChip;
  meaning: string | null;
  /** The index says a build fails without it. */
  required: boolean;
  /** The revision holds a value for it. */
  saved: boolean;
  /** A new value waits for the next run. */
  staged: boolean;
  removed: boolean;
  reenter: boolean;
  /** When the workspace set it, or null when the workspace has no value. */
  workspaceSetAt: string | null;
}

/**
 * Every name a repository's builds read, once each: the index's detected
 * names first, then what the compose file the environment is defined in
 * expects from the host, then the names set on this repository by hand, then
 * a name What runs here asked for, then the rest of the workspace's.
 */
export function repositoryVariableRows({
  index,
  saved,
  staged,
  removed,
  reenter,
  workspace,
  ask = null,
  environmentFrom = null,
}: {
  index: Pick<RepoIndex, "status" | "variables"> | null | undefined;
  saved: readonly string[];
  staged: readonly string[];
  removed: readonly string[];
  reenter: readonly string[];
  workspace: readonly WorkspaceVariable[];
  ask?: string | null;
  /** The sibling compose file's host needs (YAG-1064): required, and said to be its. */
  environmentFrom?: {
    needs: readonly string[];
    repository: { owner: string; name: string } | null;
  } | null;
}): RepositoryVariableRow[] {
  const indexed = index?.status === "indexed" ? index.variables : [];
  const composeMeaning = environmentFrom?.repository
    ? `The compose file in ${environmentFrom.repository.owner}/${environmentFrom.repository.name} expects it.`
    : "The compose file this environment is defined in expects it.";
  const detected = [
    ...indexed,
    ...(environmentFrom?.needs ?? [])
      .filter((name) => !indexed.some((variable) => variable.name === name))
      .map((name) => ({ name, meaning: composeMeaning, required: true })),
  ];
  const setHere = new Set(
    [...saved, ...staged].filter((name) => !removed.includes(name) && !reenter.includes(name)),
  );
  const workspaceSetAt = new Map(workspace.map((variable) => [variable.name, variable.setAt]));
  const meanings = new Map(detected.map((variable) => [variable.name, variable]));
  const names = [
    ...new Set([
      ...detected.map((variable) => variable.name),
      ...saved,
      ...staged,
      ...(ask ? [ask] : []),
      ...workspace.map((variable) => variable.name),
    ]),
  ];
  return names.map((name) => ({
    name,
    chip: variableChip(name, setHere, new Set(workspaceSetAt.keys())),
    meaning: meanings.get(name)?.meaning ?? null,
    required:
      meanings.get(name)?.required === true || Boolean(environmentFrom?.needs.includes(name)),
    saved: saved.includes(name),
    staged: staged.includes(name),
    removed: removed.includes(name),
    reenter: reenter.includes(name),
    workspaceSetAt: workspaceSetAt.get(name) ?? null,
  }));
}

/** The folded Variables row's summary: how many, where from, and what is missing. */
export function repositoryVariablesSummary(rows: readonly RepositoryVariableRow[]): string {
  if (!rows.length) return "None yet. The run will ask if it needs one.";
  const fromWorkspace = rows.filter((row) => row.chip === "from_workspace").length;
  const missing = rows.filter((row) => row.chip === "missing");
  const required = missing.filter((row) => row.required).length;
  return [
    `${rows.length} in use`,
    fromWorkspace ? `${fromWorkspace} from the workspace` : null,
    missing.length ? `${missing.length} missing${required ? ` (${required} required)` : ""}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The saved names that still have no value: cleared when GitHub was
 * disconnected, and not yet staged again or marked for removal.
 */
export function valuesToReenter(
  view: Pick<QaEnvironmentView, "missingValues"> | null | undefined,
  staged: readonly string[],
  removed: readonly string[],
): string[] {
  return (view?.missingValues ?? []).filter(
    (name) => !staged.includes(name) && !removed.includes(name),
  );
}

/** The banner over the Variables row, or null when nothing needs re-entering. */
export function valuesToReenterLine(names: readonly string[]): string | null {
  if (!names.length) return null;
  return `Re-enter ${names.length === 1 ? "this value" : "these values"}: ${names.join(", ")}. ${
    names.length === 1 ? "It was" : "They were"
  } cleared when GitHub was disconnected.`;
}

/** The checks a repository needs, split by whether a build can run them. */
export function checkLists(index: RepoIndex | null | undefined): {
  runnable: RepoIndexCheckRequirement[];
  elsewhere: RepoIndexCheckRequirement[];
} {
  // The commands CI runs are the checks themselves, not what they need.
  const all =
    index?.status === "indexed"
      ? index.checkRequirements.filter((check) => check.kind !== "check")
      : [];
  return {
    runnable: all.filter((check) => check.sandboxRunnable),
    elsewhere: all.filter((check) => !check.sandboxRunnable),
  };
}

/** The folded Checks row, or null when the index found no requirements. */
export function checkRequirementsSummary(index: RepoIndex | null | undefined): string | null {
  const { runnable, elsewhere } = checkLists(index);
  if (!runnable.length && !elsewhere.length) return null;
  return `${runnable.length} runnable in a build, ${elsewhere.length} need CI or a person`;
}

// ---------------------------------------------------------------------------
// What runs here (YAG-849): the record a setup run, a build or the bench
// leaves of what resolved, what ran and what is missing.
// ---------------------------------------------------------------------------

/** Who wrote the record, in words: "from a build". */
export const CAPABILITY_SOURCE_WORDS: Record<CapabilitySource, string> = {
  setup_run: "a setup run",
  build: "a build",
  bench: "the bench",
};

export function sourceWord(source: CapabilitySource): string {
  return CAPABILITY_SOURCE_WORDS[source] ?? "a run";
}

/** A check's last result. Red reads as failed; amber carries it, never red. */
export const CHECK_STATUS_WORDS: Record<CapabilityCheckStatus, string> = {
  green: "passed",
  red: "failed",
  could_not_run: "could not run",
};

export function checkStatusWord(status: CapabilityCheckStatus): string {
  return CHECK_STATUS_WORDS[status] ?? status;
}

export const DEPENDENCY_STATUS_WORDS: Record<CapabilityDependencyStatus, string> = {
  installed: "installed",
  failed: "failed",
  skipped: "skipped",
};

export function dependencyStatusWord(status: CapabilityDependencyStatus): string {
  return DEPENDENCY_STATUS_WORDS[status] ?? status;
}

/** A resolved tool's origin: in the sandbox image, or installed by the build. */
export function toolOriginWord(origin: "baked" | "install"): string {
  return origin === "baked" ? "built in" : "installed";
}

/**
 * The line beside the section's label: "as of Oct 9, 2026, from a build", or
 * "last seen Sep 1, 2026, from a build" once the record is past fourteen days.
 */
export function capabilityAsOf(
  record: Pick<RepoCapabilityRecord, "recordedAt" | "source">,
  now: Date = new Date(),
): string {
  const lead = capabilityRecordStale(record, now) ? "last seen" : "as of";
  return `${lead} ${formatEnvironmentDate(record.recordedAt)}, from ${sourceWord(record.source)}`;
}

/** "Oct 9, 2026 14:05", or just the date when the time is unreadable. */
export function capabilityCheckWhen(at: string): string {
  const time = formatEnvironmentTime(at);
  return time ? `${formatEnvironmentDate(at)} ${time}` : formatEnvironmentDate(at);
}

/**
 * The variable a missing credential's Set action prefills, or null when the
 * fix is not a variable (the App is not installed, or the host refused it).
 */
export function missingVariable(entry: CapabilityMissing): string | null {
  if (entry.kind !== "credential" || entry.cause !== "variable_not_set") return null;
  const name = entry.detail?.trim();
  return name && isVariableName(name) ? name : null;
}

/**
 * What the repository's Dockerfile and devcontainer say, as one sentence:
 * "The repository's Dockerfile starts from node:20-bookworm and installs
 * libpq-dev and imagemagick." Null when they say nothing.
 */
export function evidenceLine(evidence: CapabilityEvidence | null | undefined): string | null {
  if (!evidence) return null;
  const installs = [...evidence.systemPackages, ...evidence.features];
  const images = evidence.images;
  if (!installs.length && !images.length) return null;
  const parts: string[] = [];
  if (images.length) parts.push(`starts from ${joinNames([...images])}`);
  if (installs.length) parts.push(`installs ${joinNames(installs)}`);
  return `The repository's Dockerfile ${parts.join(" and ")}.`;
}

/** What Validate now does, beside its button. */
export const VALIDATE_NOW_HELP =
  "Runs tools, setup, services and verify once. Nothing is repaired.";

/** Why Validate now is held while the form has unsaved changes. */
export const VALIDATE_NOW_HELD = "Run or discard your changes to validate the saved environment.";

/**
 * How long a step took, in the shared words (`qaStepDuration`: "41 s",
 * "11 m 20 s", "2 h 5 m"): its own count, else its two stamps. Nothing while
 * it runs, or when the stamps do not read.
 */
export function stepElapsed(
  step: Pick<QaRunStep, "durationMs" | "startedAt" | "finishedAt">,
): string | null {
  const ms =
    typeof step.durationMs === "number"
      ? step.durationMs
      : step.startedAt && step.finishedAt
        ? Date.parse(step.finishedAt) - Date.parse(step.startedAt)
        : Number.NaN;
  return Number.isFinite(ms) && ms >= 0 ? qaStepDuration(ms) : null;
}

/** A failed step whose command ran out of time says so, and after how long (YAG-1137). */
export function stepRanOutLine(step: Pick<QaRunStep, "state" | "timedOutAfterMs">): string | null {
  return step.state === "failed" && typeof step.timedOutAfterMs === "number"
    ? `ran out of time after ${qaStepDuration(step.timedOutAfterMs)}`
    : null;
}

/** Why a system's run went ahead alone, as `QaValidation.system.reason` says it. */
export const SYSTEM_ALONE_WORDS: Record<string, string> = {
  not_member: "not a member of the system",
  unreadable: "the system could not be read",
  missing: "the system is gone",
};

/** A run's system as the server records it; `applied`, `reason` and `composeSkipped` are optional. */
export interface RunSystemFacts {
  name: string;
  applied?: boolean;
  reason?: string;
  composeSkipped?: string;
}

/** Why none of a system's compose services started for a run inside it (YAG-1139). */
export const SYSTEM_COMPOSE_SKIP_WORDS: Record<string, string> = {
  target_holds_compose: "this repository holds the compose file",
};

/**
 * Whether a run inside a system ran there: "in system gpx", or "alone: the
 * system is gone", and, inside it, when none of the system's compose
 * services started and why. Null for a run that was not a system's.
 */
export function runSystemLine(system: RunSystemFacts | null | undefined): string | null {
  if (!system) return null;
  if (system.applied === false) return `alone: ${systemAloneWords(system.reason)}`;
  if (system.composeSkipped) {
    const why = SYSTEM_COMPOSE_SKIP_WORDS[system.composeSkipped];
    return `in system ${system.name}, without its compose services${why ? `: ${why}` : ""}`;
  }
  return `in system ${system.name}`;
}

/** A system's reason in words; an unknown or absent one still says something true. */
export function systemAloneWords(reason: string | undefined): string {
  return (reason && SYSTEM_ALONE_WORDS[reason]) || "the system did not apply";
}

/** The run's steps in plan order; an older server sends none. */
export function runSteps(validation: QaValidation | null | undefined): QaRunStep[] {
  const steps = validation?.steps;
  return Array.isArray(steps) ? [...steps].sort((a, b) => a.index - b.index) : [];
}

/** The YAML line the run's failed step came from, for the editor to mark. */
export function failedStepLine(validation: QaValidation | null | undefined): number | null {
  const failed = runSteps(validation).find((step) => step.state === "failed");
  return typeof failed?.line === "number" ? failed.line : null;
}
